<script setup lang="ts">
import { withDefaults, computed } from 'vue';
import type { Category } from '@ilteducation/content-ref-data-types';
import useGuiStore from '@/store/gui';

const props = withDefaults(
  defineProps<{
    subject: Category;
    size?: string;
  }>(),
  {
    size: () => 'large',
  },
);
const emit = defineEmits<{
  (e: 'open-category', slug: string): void;
}>();

const onCategoryClick = (subject: Category) => {
  emit('open-category', subject.slug!);
};

const subjectIcon = computed(
  // @TODO subject.slug needs to be replaced with subject.icon as slugs are different in different markets
  () => new URL(`/src/assets/images/icons/icon-begreppa-subject-${props.subject.slug}.svg`, import.meta.url).href,
);
const subjectBackground = computed(
  () => `url(${new URL(`/src/assets/images/patterns/square/${props.subject.color}.png`, import.meta.url).href})`,
);
const { getThemeName } = useGuiStore();
</script>

<template>
  <v-theme-provider :theme="getThemeName(subject.slug)" with-background class="bg-transparent">
    <button
      class="w-100 category-square d-flex flex-column justify-space-evenly align-center rounded gr-1 px-1"
      :class="[`category-square__${size} bg-subject-original`, { 'px-lg-3': size === 'large' }]"
      @click="onCategoryClick(subject)"
      @keydown.enter.space="onCategoryClick(subject)"
    >
      <v-img
        v-if="size === 'large' && !subject.slug?.includes('test')"
        width="100%"
        max-width="80"
        :max-height="45"
        style="z-index: 1"
        :src="subjectIcon"
        aria-hidden="true"
        alt=""
      />
      <span class="h3">{{ subject.category }}</span>
    </button>
  </v-theme-provider>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.category-square {
  position: relative;
  overflow: hidden;
  text-align: center;
  background-image: v-bind(subjectBackground);
  background-position: center center;
  background-size: cover;
  cursor: pointer;

  span {
    color: #fff;
    z-index: 1;
    font-size: 1.2rem;
    line-height: 1;
    letter-spacing: 0.1px;
    text-shadow: 0 2px 4px rgba(18, 43, 64, 0.3);
    margin: 0;

    &:first-letter {
      text-transform: uppercase;
    }
  }

  &__small {
    height: 50px;
    padding: 5px;

    .v-img {
      display: none;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      width: auto;
    }
  }

  &__large {
    @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      height: 50px;

      .v-img {
        display: none;
      }
    }

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      height: 100px;

      .v-img {
        display: block;
        height: 40px;
      }
    }

    @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
      h3 {
        font-size: 1.45rem;
        line-height: 1;
      }
    }
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      opacity: 0.8;
      background-image: linear-gradient(180deg, transparent 0%, rgba(var(--v-theme-subject-original)));
    }
  }
}
</style>
